import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Moves an open tab onto a new deployment, at a moment that does not cost the user anything.
 *
 * The service worker downloads a new version in the background but keeps serving the old one
 * to tabs already running, which is what makes a running tab safe. The tab only breaks when
 * it asks for a lazy chunk the deploy has deleted — and it only does that when the user
 * navigates.
 *
 * So navigation is exactly where to intervene: the first route change after a new version
 * arrives is turned into a full page load of that same route. The user gets where they were
 * going, on the new bundle, and never sees the reload as an interruption — as opposed to
 * reloading the moment the version lands, which throws away whatever they were typing.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly router = inject(Router);

  private pending = false;

  start(): void {
    if (!this.updates.isEnabled) {
      return;
    }

    this.updates.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => {
        this.pending = true;
      });

    // The worker's cache is damaged beyond repair — usually a file it needs was deleted by a
    // deploy while it was mid-download. Nothing this tab does can mend it, and a normal
    // reload will, so it is worth taking the interruption immediately.
    this.updates.unrecoverable.subscribe((event) => {
      console.warn('The service worker cache is unusable, reloading:', event.reason);
      window.location.reload();
    });

    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event) => {
        if (!this.pending) {
          return;
        }
        // Guarded, because assigning the location fires another NavigationStart before the
        // browser tears the page down.
        this.pending = false;
        window.location.assign(event.url);
      });
  }
}
